import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Input from "@/v2-ui/input"
import Switch from "@/v2-ui/switch"
import Textarea from "@/v2-ui/textarea"
import Select from "@/v2-ui/select"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigCategoryUpsertMutation } from "@/v2-backoffice-client/uptime/configCategory/upsert/uptime.configCategory.upsert.mutation"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Platform = UptimeConfigQuery["getStatusConfig"]["Platforms"][number]
type Category = UptimeConfigQuery["getStatusConfig"]["Categories"][number]

export type UptimeConfigCategoryDialogProps = DialogProps & {
  mode: "create" | "edit"
  platforms: Platform[]
  category?: Category | null
}

const FORM_ID = "uptime-config-category-form"

function makeInitial(category: Category | null | undefined) {
  return {
    id: category?.Id ?? "",
    platformId: category?.PlatformId ?? "",
    name: category?.Name ?? "",
    description: category?.Description ?? "",
    priority: category?.Priority ?? 0,
    isEnabled: category?.IsEnabled ?? true,
    isSharedAcrossPlatforms: category?.IsSharedAcrossPlatforms ?? false,
    hasLayerSubCategories: category?.HasLayerSubCategories ?? false,
    // Newline-separated raw text so the operator can paste a list
    // straight from a manifest. We split + trim before submit.
    sourcesText: (category?.Sources ?? []).join("\n"),
  }
}

function parseSources(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

function UptimeConfigCategoryDialog(props: UptimeConfigCategoryDialogProps) {
  const { mode, platforms, category, open, onOpenChange, ...dialogProps } =
    props

  const [form, setForm] = useState(() => makeInitial(category))
  const [upsert, { loading, error, reset }] =
    useUptimeConfigCategoryUpsertMutation({
      onCompleted() {
        onOpenChange?.(false)
      },
    })

  useEffect(() => {
    if (open) {
      setForm(makeInitial(category))
      reset()
    }
  }, [open, category, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {mode === "edit" ? "Edit category" : "New category"}
          </Dialog.Title>
          <Dialog.Description>
            Categories link incoming probe sources to a platform card.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id={FORM_ID}
          className="uptime-config-form"
          onSubmit={(e) => {
            e.preventDefault()
            upsert({
              variables: {
                input: {
                  id: form.id.trim(),
                  platformId: form.platformId,
                  name: form.name.trim(),
                  description:
                    form.description.trim() === ""
                      ? null
                      : form.description.trim(),
                  isEnabled: form.isEnabled,
                  priority: form.priority,
                  isSharedAcrossPlatforms: form.isSharedAcrossPlatforms,
                  hasLayerSubCategories: form.hasLayerSubCategories,
                  sources: parseSources(form.sourcesText),
                },
              },
            })
          }}
        >
          <div className="uptime-config-form-row">
            <div className="uptime-config-form-field">
              <Label htmlFor="uptime-config-category-id">ID</Label>
              <Input
                id="uptime-config-category-id"
                type="text"
                required
                autoFocus={mode === "create"}
                disabled={mode === "edit"}
                placeholder="e.g. v2-apps"
                value={form.id}
                onChange={(e) => {
                  setForm({ ...form, id: e.target.value })
                }}
              />
            </div>

            <div className="uptime-config-form-field">
              <Label htmlFor="uptime-config-category-platform">Platform</Label>
              <Select
                value={form.platformId}
                onValueChange={(v) => {
                  setForm({ ...form, platformId: v })
                }}
              >
                <Select.Trigger id="uptime-config-category-platform">
                  <Select.Value placeholder="Select a platform..." />
                </Select.Trigger>
                <Select.Content>
                  {platforms.map((p) => (
                    <Select.Item key={p.Id} value={p.Id}>
                      {p.Name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </div>
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-category-name">Display name</Label>
            <Input
              id="uptime-config-category-name"
              type="text"
              required
              placeholder="e.g. Production Apps"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value })
              }}
            />
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-category-description">
              Description
            </Label>
            <Textarea
              id="uptime-config-category-description"
              rows={2}
              placeholder="Short customer-facing description (optional)"
              value={form.description}
              onChange={(e) => {
                setForm({ ...form, description: e.target.value })
              }}
            />
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-category-sources">Sources</Label>
            <Textarea
              id="uptime-config-category-sources"
              rows={4}
              placeholder={"app.release\napp.staging"}
              className="font-mono text-xs"
              value={form.sourcesText}
              onChange={(e) => {
                setForm({ ...form, sourcesText: e.target.value })
              }}
            />
            <p className="uptime-config-form-hint">
              One source per line (or comma-separated). Probe rows whose
              `Source` matches any of these are bucketed under this category.
            </p>
          </div>

          <div className="uptime-config-form-row">
            <div className="uptime-config-form-field">
              <Label htmlFor="uptime-config-category-priority">Priority</Label>
              <Input
                id="uptime-config-category-priority"
                type="number"
                required
                value={form.priority}
                onChange={(e) => {
                  setForm({
                    ...form,
                    priority: Number.parseInt(e.target.value, 10) || 0,
                  })
                }}
              />
            </div>
          </div>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-category-enabled"
          >
            <span>Enabled</span>
            <Switch
              id="uptime-config-category-enabled"
              checked={form.isEnabled}
              onCheckedChange={(checked) => {
                setForm({ ...form, isEnabled: checked === true })
              }}
            />
          </Label>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-category-shared"
          >
            <div className="uptime-config-form-toggle-text">
              <span className="uptime-config-form-toggle-title">
                Shared across platforms
              </span>
              <span className="uptime-config-form-toggle-hint">
                Render this category on every platform card, not just its owning
                platform.
              </span>
            </div>
            <Switch
              id="uptime-config-category-shared"
              checked={form.isSharedAcrossPlatforms}
              onCheckedChange={(checked) => {
                setForm({
                  ...form,
                  isSharedAcrossPlatforms: checked === true,
                })
              }}
            />
          </Label>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-category-layer-sub"
          >
            <div className="uptime-config-form-toggle-text">
              <span className="uptime-config-form-toggle-title">
                Layer sub-categories
              </span>
              <span className="uptime-config-form-toggle-hint">
                Render the v2-console-style nested layer buckets under this
                category.
              </span>
            </div>
            <Switch
              id="uptime-config-category-layer-sub"
              checked={form.hasLayerSubCategories}
              onCheckedChange={(checked) => {
                setForm({
                  ...form,
                  hasLayerSubCategories: checked === true,
                })
              }}
            />
          </Label>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            isLoading={loading}
            disabled={form.platformId === ""}
          >
            {mode === "edit" ? "Save" : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeConfigCategoryDialog
